import type { CSSProperties } from "react";
import { appTheme } from "../../../theme";

/** An isolated, light employer-branded surface, including before/after login. */
export function recruitingBrandStyles(color?: string): CSSProperties {
  const base = /^#[0-9a-f]{6}$/i.test(color || "") ? color! : "#334155";
  const rgb = [1, 3, 5].map((start) =>
    parseInt(base.slice(start, start + 2), 16),
  );
  const luminance = rgb
    .map((value) => {
      const channel = value / 255;
      return channel <= 0.04045
        ? channel / 12.92
        : ((channel + 0.055) / 1.055) ** 2.4;
    })
    .reduce(
      (sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index],
      0,
    );
  const foreground = luminance > 0.179 ? "#000000" : "#ffffff";
  const readable = luminance > 0.179 ? "#243041" : base;
  const tint = `rgba(${rgb.join(",")},0.09)`;
  const border = `rgba(${rgb.join(",")},0.4)`;
  const palette: Record<string, string> = {
    solidBg: base,
    solidHoverBg: base,
    solidActiveBg: base,
    solidColor: foreground,
    outlinedColor: readable,
    outlinedBorder: border,
    outlinedHoverBg: tint,
    outlinedActiveBg: tint,
    softColor: readable,
    softBg: tint,
    softHoverBg: tint,
    softActiveBg: tint,
    plainColor: readable,
    plainHoverBg: tint,
    plainActiveBg: tint,
    mainChannel: rgb.join(" "),
    lightChannel: rgb.join(" "),
    darkChannel: rgb.join(" "),
  };
  for (const shade of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]) {
    palette[String(shade)] = shade < 300 ? tint : base;
  }
  return {
    ...appTheme.generateCssVars("light").css,
    ...Object.fromEntries(
      Object.entries(palette).map(([key, value]) => [
        `--joy-palette-primary-${key}`,
        value,
      ]),
    ),
    "--joy-palette-focusVisible": readable,
    colorScheme: "light",
  } as CSSProperties;
}
